/**
 * DEMO · Idea 10 — ¿Este plano sirve para presupuestar?
 *
 * Real (del archivo): el diagnóstico de los dos IFC se calcula en el navegador, $0, con las reglas del
 * estándar IFC (cantidades propias, tipo, clase genérica, nivel, material). La versión "mal exportada"
 * se generó a propósito para el demo. Los DXF y PDF de ejemplo (generados para el demo, no de clientes)
 * también se leen de verdad: unidades, capas, bloques, cotas; vectorial o escaneado. De ejemplo: umbrales
 * del veredicto y los textos del veredicto del PDF.
 */
import { useEffect, useMemo, useState } from 'react';
import { Spinner } from '@fluentui/react-components';
import { ArrowUpload20Regular, Checkmark20Filled, Warning20Filled, DismissCircle20Filled } from '@fluentui/react-icons';
import { DemoVisor3D, type Pintura } from '../../components/demos/DemoVisor3D.js';
import { DemoMarco, NotaEjemplo, Panel } from '../../components/demos/DemoMarco.js';
import type { ElementoDemo, ModeloDemo } from '../../components/demos/modeloDemo.js';
import { coloresDemo, nombreClase } from '../../components/demos/logicaDemo.js';
import { diagnosticarDxf, diagnosticarPdf, type DiagnosticoDxf, type DiagnosticoPdf } from '../../components/demos/diagnostico2d.js';
import datos from '../../components/demos/datos/chequeo-del-modelo.json';
import catalogo from '../../components/demos/datos/catalogo.json';

const FICHA = catalogo.demos.find((d) => d.id === 'chequeo-del-modelo')!;
type Veredicto = 'bien' | 'cuidado' | 'no';
interface Archivo {
  id: string;
  nombre: string;
  detalle: string;
  tipo: string;
  url?: string;
  veredicto?: string;
  titulo?: string;
  lineas?: string[];
  vista?: string;
}
const ARCHIVOS = datos.archivos as Archivo[];

const pct = (n: number, d: number) => (d ? (n / d) * 100 : 0);
const esGenerico = (e: ElementoDemo) => e.clase === 'IfcBuildingElementProxy';
const estaListo = (e: ElementoDemo) => e.tieneCantidadesPropias && e.tieneTipo && !esGenerico(e) && e.nivelId != null;

function diagnosticar(m: ModeloDemo) {
  const els = m.elementos;
  const n = els.length;
  const cuenta = (f: (e: ElementoDemo) => boolean) => els.filter(f).length;
  const conCantidades = cuenta((e) => e.tieneCantidadesPropias);
  const sinTipo = cuenta((e) => !e.tieneTipo);
  const genericos = cuenta(esGenerico);
  const sinNivel = cuenta((e) => e.nivelId == null);
  const sinMaterial = cuenta((e) => !e.tieneMaterial);
  const listos = cuenta(estaListo);
  const pl = pct(listos, n);
  const veredicto: Veredicto =
    pl >= datos.umbrales.bien.listosMin ? 'bien' : pl >= datos.umbrales.cuidado.listosMin ? 'cuidado' : 'no';
  const a = datos.avisarSi;
  const razones: string[] = [];
  if (pct(n - conCantidades, n) > a.sinCantidadesPct)
    razones.push(`${Math.round(pct(n - conCantidades, n))} % de las piezas no trae sus cantidades: habría que medirlas del dibujo y revisarlas.`);
  if (pct(genericos, n) > a.genericosPct)
    razones.push(`${genericos} piezas vienen como "genéricas": el modelo no dice si son muro, viga o ventana.`);
  if (pct(sinTipo, n) > a.sinTipoPct) razones.push(`${sinTipo} piezas no dicen de qué tipo son, así que no se pueden agrupar para cotizar.`);
  if (pct(sinNivel, n) > a.sinNivelPct) razones.push(`${sinNivel} piezas no dicen en qué piso están.`);
  if (pct(sinMaterial, n) > a.sinMaterialPct) razones.push(`${sinMaterial} piezas no dicen de qué material son.`);
  return { n, conCantidades, sinTipo, genericos, sinNivel, sinMaterial, listos, veredicto, razones };
}

const VEREDICTO: Record<Veredicto, { titulo: string; color: string; fondo: string; icono: JSX.Element }> = {
  bien: { titulo: 'Sirve bien para presupuestar', color: 'var(--ok)', fondo: 'var(--ok-weak)', icono: <Checkmark20Filled /> },
  cuidado: { titulo: 'Sirve, con cuidado', color: 'var(--warn)', fondo: 'var(--warn-weak)', icono: <Warning20Filled /> },
  no: { titulo: 'Casi no sirve para presupuestar', color: 'var(--bad)', fondo: 'var(--bad-weak)', icono: <DismissCircle20Filled /> },
};

export function DemoChequeoModelo() {
  const [elegido, setElegido] = useState<Archivo>(ARCHIVOS[1]!);
  const [revisando, setRevisando] = useState(true);
  const [modelos, setModelos] = useState<Record<string, ModeloDemo>>({});
  const colores = useMemo(() => coloresDemo(), []);

  useEffect(() => {
    setRevisando(true);
    const t = setTimeout(() => setRevisando(false), datos.revision.ms);
    return () => clearTimeout(t);
  }, [elegido]);

  // Planos 2D: se descarga el archivo y se lee con las reglas de su formato.
  const [diag2d, setDiag2d] = useState<{ id: string; dxf?: DiagnosticoDxf; pdf?: DiagnosticoPdf } | null>(null);
  useEffect(() => {
    if (elegido.tipo === 'ifc' || !elegido.url) return;
    let vivo = true;
    fetch(elegido.url)
      .then((r) => r.arrayBuffer())
      .then((b) => {
        if (!vivo) return;
        if (elegido.tipo === 'dxf') setDiag2d({ id: elegido.id, dxf: diagnosticarDxf(new TextDecoder().decode(b)) });
        else setDiag2d({ id: elegido.id, pdf: diagnosticarPdf(new Uint8Array(b)) });
      })
      .catch(() => undefined);
    return () => {
      vivo = false;
    };
  }, [elegido]);
  const d2 = diag2d?.id === elegido.id ? diag2d : null;

  const modelo = elegido.tipo === 'ifc' ? modelos[elegido.id] : undefined;
  const diag = useMemo(() => (modelo ? diagnosticar(modelo) : null), [modelo]);
  const listoParaVer = !revisando && (elegido.tipo === 'ifc' ? diag : d2);

  const pintar = useMemo(
    () =>
      (el: ElementoDemo | null): Pintura => {
        if (!el) return { opacidad: 0.15 };
        if (esGenerico(el)) return { color: colores.bad, opacidad: 1 };
        if (!el.tieneCantidadesPropias || !el.tieneTipo || el.nivelId == null) return { color: colores.warn, opacidad: 1 };
        return { color: colores.neutro, opacidad: 0.35 };
      },
    [colores],
  );

  return (
    <DemoMarco titulo={FICHA.nombre} dolor={FICHA.dolor}>
      <div style={{ display: 'grid', gridTemplateColumns: '280px minmax(0, 1.2fr) minmax(340px, 1fr)', gap: 14 }}>
        <Panel titulo="Sube un archivo">
          <div
            style={{
              border: '1px dashed var(--border-strong)',
              borderRadius: 'var(--radius-md)',
              padding: 12,
              textAlign: 'center',
              color: 'var(--text-tertiary)',
              fontSize: 'var(--fs-200)',
            }}
          >
            <ArrowUpload20Regular />
            <div>En el demo, elige uno de ejemplo:</div>
          </div>
          {ARCHIVOS.map((a) => (
            <button
              key={a.id}
              onClick={() => setElegido(a)}
              style={{
                textAlign: 'left',
                background: elegido.id === a.id ? 'var(--accent-weak)' : 'var(--bg-raised)',
                border: `1px solid ${elegido.id === a.id ? 'var(--accent)' : 'var(--border-subtle)'}`,
                borderRadius: 'var(--radius-sm)',
                padding: '8px 10px',
                color: 'var(--text-primary)',
                cursor: 'pointer',
              }}
            >
              <div style={{ fontWeight: 600, fontSize: 'var(--fs-300)', wordBreak: 'break-all' }}>{a.nombre}</div>
              <div style={{ fontSize: 'var(--fs-200)', color: 'var(--text-tertiary)' }}>{a.detalle}</div>
            </button>
          ))}
          <NotaEjemplo>Revisarlo no gasta nada: son reglas del formato, no inteligencia artificial.</NotaEjemplo>
        </Panel>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, minWidth: 0 }}>
          {elegido.tipo === 'ifc' ? (
            <>
              <DemoVisor3D
                key={elegido.id}
                fuentes={[{ clave: elegido.id, url: elegido.url! }]}
                onListo={(m) => setModelos((prev) => ({ ...prev, ...m }))}
                pintar={pintar}
                alto={480}
              />
              <div style={{ display: 'flex', gap: 16, fontSize: 'var(--fs-200)', color: 'var(--text-secondary)', flexWrap: 'wrap' }}>
                <Muestra color="var(--bad)" texto="Pieza genérica (no dice qué es)" />
                <Muestra color="var(--warn)" texto="Le falta algo: cantidades, tipo o piso" />
                <Muestra color="var(--text-tertiary)" texto="Completa" />
              </div>
            </>
          ) : (
            <div
              style={{
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--border-subtle)',
                background: '#fff',
                overflow: 'hidden',
              }}
            >
              <img src={elegido.vista} alt={`Vista del archivo ${elegido.nombre}`} style={{ width: '100%', display: 'block' }} />
            </div>
          )}
          {elegido.tipo !== 'ifc' && (
            <a href={elegido.url} target="_blank" rel="noreferrer" style={{ fontSize: 'var(--fs-200)', color: 'var(--accent)' }}>
              Abrir el archivo de ejemplo
            </a>
          )}
        </div>

        <Panel titulo="Diagnóstico">
          <div style={{ fontSize: 'var(--fs-200)', color: 'var(--text-tertiary)', wordBreak: 'break-all' }}>{elegido.nombre}</div>
          {!listoParaVer ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-secondary)', padding: '24px 0' }}>
              <Spinner size="tiny" /> Revisando el archivo…
            </div>
          ) : elegido.tipo === 'ifc' && diag ? (
            <>
              <Tarjeta v={diag.veredicto} />
              <div style={{ fontSize: 'var(--fs-300)' }}>
                Con este modelo podrías sacar <b>{Math.round(pct(diag.listos, diag.n))} %</b> del presupuesto directo del
                archivo. Lo demás habría que revisarlo a mano.
              </div>
              <Indicador etiqueta="Piezas con sus cantidades" valor={pct(diag.conCantidades, diag.n)} bueno />
              <Indicador etiqueta="Piezas sin tipo" valor={pct(diag.sinTipo, diag.n)} cuenta={diag.sinTipo} />
              <Indicador etiqueta={`Piezas genéricas (${nombreClase('IfcBuildingElementProxy').toLowerCase()})`} valor={pct(diag.genericos, diag.n)} cuenta={diag.genericos} />
              <Indicador etiqueta="Piezas sin piso" valor={pct(diag.sinNivel, diag.n)} cuenta={diag.sinNivel} />
              <Indicador etiqueta="Piezas sin material" valor={pct(diag.sinMaterial, diag.n)} cuenta={diag.sinMaterial} />
              {diag.razones.length > 0 ? (
                <ul style={{ margin: 0, paddingLeft: 18, color: 'var(--text-secondary)', fontSize: 'var(--fs-300)' }}>
                  {diag.razones.map((r) => (
                    <li key={r}>{r}</li>
                  ))}
                </ul>
              ) : (
                <div style={{ color: 'var(--text-secondary)', fontSize: 'var(--fs-300)' }}>No encontramos nada que te vaya a frenar.</div>
              )}
              <NotaEjemplo>
                Revisamos {diag.n} piezas. Los porcentajes se midieron del archivo; el criterio para el veredicto es de ejemplo.
              </NotaEjemplo>
            </>
          ) : d2?.dxf ? (
            <DiagnosticoDeDxf d={d2.dxf} />
          ) : d2?.pdf ? (
            <DiagnosticoDePdf d={d2.pdf} />
          ) : null}
        </Panel>
      </div>
    </DemoMarco>
  );
}

function Tarjeta({ v, titulo }: { v: Veredicto; titulo?: string }) {
  const x = VEREDICTO[v];
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        background: x.fondo,
        color: x.color,
        borderRadius: 'var(--radius-sm)',
        padding: '10px 12px',
        fontWeight: 700,
        fontSize: 'var(--fs-400)',
      }}
    >
      {x.icono}
      {titulo ?? x.titulo}
    </div>
  );
}

function Indicador({ etiqueta, valor, cuenta, bueno }: { etiqueta: string; valor: number; cuenta?: number; bueno?: boolean }) {
  const color = bueno ? 'var(--ok)' : valor > 0 ? 'var(--warn)' : 'var(--ok)';
  return (
    <div>
      <div style={{ display: 'flex', fontSize: 'var(--fs-200)', color: 'var(--text-secondary)' }}>
        <span style={{ flex: 1 }}>{etiqueta}</span>
        <span style={{ fontVariantNumeric: 'tabular-nums', color: 'var(--text-primary)' }}>
          {Math.round(valor)} %{cuenta != null ? ` · ${cuenta}` : ''}
        </span>
      </div>
      <div style={{ height: 6, background: 'var(--bg-raised)', borderRadius: 3, marginTop: 3 }}>
        <div style={{ width: `${Math.min(100, valor)}%`, height: '100%', background: color, borderRadius: 3 }} />
      </div>
    </div>
  );
}

function Muestra({ color, texto }: { color: string; texto: string }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span style={{ width: 12, height: 12, borderRadius: 3, background: color }} />
      {texto}
    </span>
  );
}

function Dato({ ok, texto }: { ok: boolean; texto: string }) {
  return (
    <div style={{ display: 'flex', gap: 8, fontSize: 'var(--fs-300)', alignItems: 'flex-start' }}>
      <span style={{ color: ok ? 'var(--ok)' : 'var(--bad)', flexShrink: 0 }}>{ok ? <Checkmark20Filled /> : <DismissCircle20Filled />}</span>
      <span>{texto}</span>
    </div>
  );
}

function DiagnosticoDeDxf({ d }: { d: DiagnosticoDxf }) {
  const pctCapas = pct(d.enCapasConNombre, d.entidades);
  const capasOk = pctCapas >= datos.dxf.capasConNombreMinPct;
  const puntos = [!!d.unidades, capasOk, d.bloques > 0, d.cotas > 0 || d.textos > 0];
  const buenos = puntos.filter(Boolean).length;
  const v: Veredicto = !d.unidades ? (buenos >= 2 ? 'cuidado' : 'no') : buenos === puntos.length ? 'bien' : 'cuidado';
  return (
    <>
      <Tarjeta v={v} />
      <Dato ok={!!d.unidades} texto={d.unidades ? `Dice sus unidades: está en ${d.unidades}.` : 'No dice en qué unidades está: una medida podría salir 100 o 1,000 veces más grande o más chica. Te preguntaríamos una medida conocida para fijar la escala.'} />
      <Dato
        ok={capasOk}
        texto={
          d.capas.length
            ? `${Math.round(pctCapas)} % del dibujo está en capas con nombre (${d.capas.join(', ')}): se puede saber qué es muro, qué es puerta y qué es cota.`
            : 'Todo está en la capa 0: no se puede saber qué línea es muro, cuál es puerta y cuál es cota.'
        }
      />
      <Dato ok={d.bloques > 0} texto={d.bloques > 0 ? `${d.bloques} puertas y ventanas vienen como bloques: se cuentan solas.` : `No hay bloques: el dibujo son ${d.lineasSueltas} líneas sueltas y las puertas y ventanas habría que contarlas a mano.`} />
      <Dato ok={d.cotas > 0} texto={d.cotas > 0 ? `${d.cotas} cotas y ${d.textos} textos (nombres de cuartos): sirven para revisar medidas y saber qué es cada espacio.` : 'No trae cotas ni nombres de cuartos.'} />
      <NotaEjemplo>Revisamos {d.entidades} elementos del dibujo. El criterio para el veredicto es de ejemplo.</NotaEjemplo>
    </>
  );
}

function DiagnosticoDePdf({ d }: { d: DiagnosticoPdf }) {
  const x = d.vectorial ? datos.pdf.vectorial : datos.pdf.escaneado;
  return (
    <>
      <Tarjeta v={x.veredicto as Veredicto} titulo={x.titulo} />
      <Dato ok={d.vectorial} texto={d.vectorial ? `Es un PDF de dibujo: trae ${d.trazos} trazos y ${d.textos} textos que se pueden leer.` : `Es un PDF escaneado: la página es ${d.imagenes === 1 ? 'una imagen' : `${d.imagenes} imágenes`}, sin trazos que se puedan medir.`} />
      <ul style={{ margin: 0, paddingLeft: 18, color: 'var(--text-secondary)', fontSize: 'var(--fs-300)' }}>
        {x.lineas.map((r) => (
          <li key={r}>{r}</li>
        ))}
      </ul>
      <NotaEjemplo>Vectorial o escaneado se lee del archivo; los textos del veredicto son de ejemplo.</NotaEjemplo>
    </>
  );
}
